import { formatPhone } from '@fundline/shared/phone';
import { AddNumberRequestSchema } from '@fundline/shared/schemas/numbers';
import { type FormEvent, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { TextField } from '../../components/ui/Field.tsx';
import { Card, EmptyState, PageHeader, Spinner } from '../../components/ui/Layout.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import { SettingsTabs } from './SettingsTabs.tsx';
import { type PhoneNumber, useAddNumber, useNumbersQuery } from './use-numbers.ts';

const STATUS_LABELS: Record<string, string> = {
  ordering: 'Ordering',
  active: 'Active',
  paused: 'Paused',
  released: 'Released',
};

export function NumbersSettingsPage() {
  const { workspaceId } = useWorkspaceClaims();
  const numbers = useNumbersQuery(workspaceId);
  const add = useAddNumber(workspaceId);
  const [areaCode, setAreaCode] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    const parsed = AddNumberRequestSchema.safeParse({ areaCode });
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Enter an area code');
      return;
    }
    setFieldError(null);
    try {
      const added = await add.mutateAsync(parsed.data);
      setNotice(`Added ${formatPhone(added.e164)}.`);
      setAreaCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't add the number.");
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Workspace settings"
        description="Sending numbers. Each merchant always hears from the same number."
      />
      <SettingsTabs />

      <Card title="Add a number">
        <form className="flex flex-col gap-3" onSubmit={(e) => void submit(e)} noValidate>
          <Alert tone="info">
            Test numbers only for now: they use the fictional 555-01XX range and send nothing to
            real phones. Buying real numbers comes with 10DLC registration.
          </Alert>
          <div className="max-w-48">
            <TextField
              label="Area code"
              inputMode="numeric"
              autoComplete="off"
              maxLength={3}
              placeholder="212"
              value={areaCode}
              onChange={(e) => setAreaCode(e.target.value)}
              error={fieldError}
            />
          </div>
          <div>
            <Button type="submit" busy={add.isPending}>
              Add number
            </Button>
          </div>
        </form>
      </Card>

      <section className="flex flex-col gap-3" aria-label="Numbers">
        <h2 className="body font-semibold text-ink">Numbers</h2>
        {error ? <Alert tone="error">{error}</Alert> : null}
        {notice ? <Alert tone="success">{notice}</Alert> : null}
        {numbers.isPending ? <Spinner label="Loading numbers" /> : null}
        {numbers.isError ? <Alert tone="error">{numbers.error.message}</Alert> : null}
        {numbers.data?.length === 0 ? (
          <EmptyState title="No numbers yet">Add one above to start texting.</EmptyState>
        ) : null}
        {numbers.data && numbers.data.length > 0 ? (
          <ul className="flex flex-col divide-y divide-hairline rounded-lg border border-hairline bg-surface-raised">
            {numbers.data.map((n: PhoneNumber) => (
              <li
                key={n.id}
                className="flex flex-wrap items-center justify-between gap-2 p-3"
                data-testid="number-row"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="font-medium text-ink">{formatPhone(n.e164)}</span>
                  <span className="footnote text-ink-muted">
                    {n.provider === 'fake' ? 'Test number' : 'Telnyx'}
                    {n.state ? ` · ${n.state}` : ''} · up to {n.maxPerDay.toLocaleString()} texts a
                    day
                  </span>
                </span>
                <span className="flex items-center gap-3 callout text-ink-muted">
                  <span>
                    {n.merchantCount} {n.merchantCount === 1 ? 'merchant' : 'merchants'}
                  </span>
                  <span className="rounded-pill bg-surface px-2 py-0.5 footnote font-medium text-ink-muted">
                    {STATUS_LABELS[n.status] ?? n.status}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </div>
  );
}
